/**
 * OrgWorkspacePicker — switch-first workspace picker.
 *
 * Design lock (replaces the prior two-column hover/preview model):
 *
 *   - GitHub-style flat list. Workspaces are the rows; orgs are subtle
 *     section headers grouping them. Single-org users see no headers
 *     at all (just workspaces), with a small read-only "in <Org>"
 *     cue at the top so the tenant context stays visible.
 *   - Single search input filters across every workspace the caller
 *     can reach, regardless of which org they live in. Headers
 *     collapse to only the orgs with surviving matches. The
 *     `+ New workspace` footer is suppressed during search.
 *   - Inline `+ New workspace in <Org>` footer per org section,
 *     role-gated via `useCanI('workspaces.create', orgId)` — same
 *     matrix the backend `requireOrgPermission` middleware reads, so
 *     UI affordance cannot drift from API enforcement.
 *   - Keyboard nav: search input auto-focuses on open; ↑/↓ moves
 *     between workspace rows; Enter switches; Esc closes.
 *
 * Org creation is gone from this picker — moved to /admin/orgs as a
 * superadmin-only privileged provisioning action. The picker switches
 * between existing orgs+workspaces; it doesn't manufacture new
 * tenants. See ADR 0010 Appendix B + `routes/admin/orgs.ts` for the
 * rationale.
 *
 * Three trigger variants share the popover internals:
 *
 *   - `'inline'` (Settings page placement) — card trigger at the top
 *     of the Settings page with an "Active context" label +
 *     `Org › Workspace` crumb + "Switch" cue + chevron. Popover
 *     anchors below the trigger in-place (no portal — the Settings
 *     page container doesn't horizontally clip).
 *   - `'header-tile'` (always-visible global switcher) — compact
 *     header pill matching the height of the app/variant dropdown.
 *     Small 28×28 colored tile (initial-letter glyph hashed from the
 *     workspace id) + workspace name + chevron. Mounts to the LEFT
 *     of the app/variant dropdown so switching is one click from
 *     any page; the pill shape sits comfortably in the header row
 *     without dominating the icon-stacked sidebar's aesthetic.
 *     Popover anchors below the trigger in-place (the header doesn't
 *     clip horizontally either, so no portal needed).
 *   - `'header'` (legacy) — collapsed breadcrumb `Org › Workspace ▾`.
 *     Kept for back-compat with any caller that doesn't pass a
 *     variant; not used by any current production surface.
 *
 * Active org is derived from the active workspace's `orgId` (URL is
 * the source of truth). No localStorage, no per-user "last active
 * org" state.
 */
import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Plus, X, Search } from 'lucide-react'
import {
  workspacesAPI,
  type WorkspaceSummary,
  type OrgWithRole,
} from '@/lib/api'
import { useIdentity } from '@/hooks/useIdentity'
import { useCanI } from '@/hooks/useCanI'
import {
  extractInitial,
  getWorkspaceTile,
} from '@/lib/workspaceVisual'

interface OrgWorkspacePickerProps {
  /** The currently-active workspace id from the URL — drives which
   * workspace is highlighted with a leading dot + "current" pip. */
  activeWorkspaceId: string
  /** Render variant — three options, all sharing the popover
   * content and state internals; only the trigger shape + popover
   * anchoring differ. See the file header for variant semantics.
   * Default is `'header'` for back-compat. */
  variant?: 'header' | 'inline' | 'header-tile'
}

interface OrgGroup {
  org: OrgWithRole
  workspaces: WorkspaceSummary[]
}

export function OrgWorkspacePicker({
  activeWorkspaceId,
  variant = 'header',
}: OrgWorkspacePickerProps) {
  const { state } = useIdentity()
  const navigate = useNavigate()

  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [open, setOpen] = useState(false)
  const [creatingInOrgId, setCreatingInOrgId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [keyboardCursor, setKeyboardCursor] = useState<number>(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Load workspaces once (and after creates). The list is small —
  // refetching the lot is cheaper than tracking partial cache state.
  const refreshWorkspaces = useCallback(async () => {
    try {
      const list = await workspacesAPI.list()
      setWorkspaces(list)
    } catch {
      setWorkspaces([])
    }
  }, [])

  useEffect(() => {
    void refreshWorkspaces()
  }, [refreshWorkspaces])

  // Click-outside closes. Resets search + create state so a re-open
  // starts clean. Cursor resets to 0 to keep the keyboard contract
  // predictable across opens.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setCreatingInOrgId(null)
        setSearch('')
        setKeyboardCursor(0)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Auto-focus the search input when the popover opens — makes
  // typing-to-filter feel like a mini command palette.
  useEffect(() => {
    if (open) {
      // Defer one tick so the portal/transition has mounted.
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
  }, [open])

  // Identity-derived data computed before the early-return so the
  // useCanI hooks below run unconditionally (Rules of Hooks).
  const orgs = state.status === 'ready' ? state.identity.orgs : []

  // Active workspace + org derived from URL.
  const activeWorkspace = workspaces.find((w) => w._id === activeWorkspaceId)
  const activeOrg = orgs.find((o) => o._id === activeWorkspace?.orgId)

  // Group workspaces by org for the popover. Single pass; preserves
  // org-list order (from the identity payload) and within each org
  // sorts workspaces by createdAt (matches /me's default-workspace
  // pick).
  const orgGroups = useMemo<OrgGroup[]>(() => {
    const byOrg = new Map<string, WorkspaceSummary[]>()
    for (const ws of workspaces) {
      const list = byOrg.get(ws.orgId) ?? []
      list.push(ws)
      byOrg.set(ws.orgId, list)
    }
    for (const list of byOrg.values()) {
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    }
    return orgs.map((org) => ({
      org,
      workspaces: byOrg.get(org._id) ?? [],
    }))
  }, [orgs, workspaces])

  // Filtered groups — applies the search to workspace names and
  // collapses out orgs with zero matches. Empty search returns the
  // unfiltered groups.
  const visibleGroups = useMemo<OrgGroup[]>(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return orgGroups
    return orgGroups
      .map((g) => ({
        org: g.org,
        workspaces: g.workspaces.filter((w) =>
          w.name.toLowerCase().includes(needle),
        ),
      }))
      .filter((g) => g.workspaces.length > 0)
  }, [orgGroups, search])

  // Flatten the visible workspaces into a single list so the keyboard
  // cursor can walk it linearly across org boundaries. Stable ordering
  // (matches the visual top-to-bottom order in the popover).
  const flatVisibleWorkspaces = useMemo(
    () => visibleGroups.flatMap((g) => g.workspaces),
    [visibleGroups],
  )

  // Clamp the cursor whenever the visible-rows count changes (e.g. the
  // user types in the search box). Without this, the cursor can sit
  // off the end and Enter would do nothing.
  useEffect(() => {
    if (keyboardCursor >= flatVisibleWorkspaces.length) {
      setKeyboardCursor(Math.max(0, flatVisibleWorkspaces.length - 1))
    }
  }, [flatVisibleWorkspaces.length, keyboardCursor])

  // Identity may not be ready (loading/error/unauthenticated). Guard
  // sits AFTER every hook call to keep hook count + order stable
  // across renders (Rules of Hooks).
  if (state.status !== 'ready') return null

  function handlePickWorkspace(workspaceId: string) {
    setOpen(false)
    setCreatingInOrgId(null)
    setSearch('')
    setKeyboardCursor(0)
    navigate(`/workspaces/${workspaceId}/results`)
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setSearch('')
      setKeyboardCursor(0)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setKeyboardCursor((c) =>
        flatVisibleWorkspaces.length === 0
          ? 0
          : Math.min(c + 1, flatVisibleWorkspaces.length - 1),
      )
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setKeyboardCursor((c) => Math.max(c - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      const target = flatVisibleWorkspaces[keyboardCursor]
      if (target) {
        e.preventDefault()
        handlePickWorkspace(target._id)
      }
    }
  }

  // Display labels for the trigger variants.
  const orgLabel = activeOrg?.name ?? '—'
  const wsLabel = activeWorkspace?.name ?? '—'
  const initial = extractInitial(activeWorkspace?.name)
  const tile = getWorkspaceTile(activeWorkspace?._id ?? 'fallback')
  const isMultiOrg = orgs.length > 1

  // ── Trigger variants ───────────────────────────────────────────────

  const triggerHeader = (
    <button
      type="button"
      onClick={() => setOpen((prev) => !prev)}
      className="flex items-center gap-1.5 h-8 px-3 text-sm font-medium bg-muted rounded-md hover:bg-muted/80 focus:outline-hidden focus:ring-2 focus:ring-ring"
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <span>{orgLabel}</span>
      <span className="text-muted-foreground">›</span>
      <span>{wsLabel}</span>
      <ChevronDown
        className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
          open ? 'rotate-180' : ''
        }`}
      />
    </button>
  )

  const triggerInline = (
    <button
      type="button"
      onClick={() => setOpen((prev) => !prev)}
      className="w-full flex items-center gap-3 px-4 py-3 text-sm bg-card border rounded-lg hover:border-primary/50 focus:outline-hidden focus:ring-2 focus:ring-ring text-left transition-colors"
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Active context
        </div>
        <div className="font-medium truncate mt-0.5">
          {orgLabel} <span className="text-muted-foreground">›</span> {wsLabel}
        </div>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">Switch</span>
      <ChevronDown
        className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 ${
          open ? 'rotate-180' : ''
        }`}
      />
    </button>
  )

  // Header-tile — compact pill matching the height of the app/variant
  // dropdown it sits next to. 28×28 colored tile (initial-letter glyph
  // hashed from the workspace id, deterministic across sessions) +
  // workspace name + chevron. Tooltip carries the full `Org · Workspace`
  // string for tenant disambiguation when initials collide. Mounted to
  // the LEFT of the app/variant dropdown in the global header.
  const triggerHeaderTile = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen((prev) => !prev)}
      className="flex items-center gap-2 h-8 pl-1 pr-2.5 text-sm bg-muted rounded-md hover:bg-muted/80 focus:outline-hidden focus:ring-2 focus:ring-ring min-w-0"
      aria-haspopup="dialog"
      aria-expanded={open}
      title={`${orgLabel} · ${wsLabel}`}
    >
      <div
        className={`w-7 h-7 rounded flex items-center justify-center text-white font-bold text-xs border shrink-0 ${tile.gradient} ${tile.border}`}
        aria-hidden="true"
      >
        {initial}
      </div>
      {/* #1822 — `max-w-[140px]` already capped this label on wide screens, but
          a cap is not the same as being able to shrink: at 375px 140px is still
          too much to insist on. `min-w-0` lets it give as well, so the same one
          rule covers both cases instead of a second, different one for mobile.
          The full value stays in the button's `title` above. */}
      <span className="font-medium truncate max-w-[140px] min-w-0">{wsLabel}</span>
      <ChevronDown
        className={`h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0 ${
          open ? 'rotate-180' : ''
        }`}
      />
    </button>
  )

  // ── Popover body ───────────────────────────────────────────────────
  //
  // All three variants (inline / header-tile / header) anchor the
  // popover below the trigger in-place. None of the host containers
  // (Settings page, header bar) horizontally clip, so no portal is
  // needed — the popover sits as an absolutely-positioned child of
  // the trigger's nearest positioned ancestor (the `relative` wrapper
  // around the trigger).

  const popoverBody = !open ? null : (
    <div
      className="absolute top-full left-0 mt-2 bg-popover border rounded-lg shadow-lg z-50 overflow-hidden w-[360px]"
      onKeyDown={handleKeyDown}
    >
      {/* Single-org users get a small read-only "in <Org>" cue so
          tenant context stays visible even though there's no org
          column. Multi-org users skip this — the section headers
          inside the list provide the org context per row. */}
      {!isMultiOrg && activeOrg && (
        <div className="px-3 py-2 border-b text-[11px] text-muted-foreground">
          in <span className="text-foreground font-medium">{activeOrg.name}</span>
        </div>
      )}

      {/* Search — always visible. Dropping the `SEARCH_THRESHOLD = 7`
          gate so muscle memory works the same regardless of workspace
          count. Cmd-F equivalence becomes universal. */}
      <div className="px-2 py-2 border-b">
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isMultiOrg ? 'Search all workspaces…' : 'Search workspaces…'}
            className="w-full h-7 pl-7 pr-2 text-sm bg-muted/50 rounded focus:outline-hidden focus:ring-1 focus:ring-ring"
            aria-label="Search workspaces"
          />
        </div>
      </div>

      {/* Scrollable section list */}
      <div className="max-h-[360px] overflow-y-auto p-1">
        {visibleGroups.length === 0 ? (
          <div className="px-3 py-6 text-xs text-muted-foreground text-center">
            {workspaces.length === 0
              ? 'No workspaces yet.'
              : 'No workspaces match.'}
          </div>
        ) : (
          visibleGroups.map((group) => (
            <OrgSection
              key={group.org._id}
              org={group.org}
              workspaces={group.workspaces}
              isMultiOrg={isMultiOrg}
              isSearching={search.trim().length > 0}
              activeWorkspaceId={activeWorkspaceId}
              creatingInOrgId={creatingInOrgId}
              setCreatingInOrgId={setCreatingInOrgId}
              onPickWorkspace={handlePickWorkspace}
              onCreated={async (ws) => {
                setCreatingInOrgId(null)
                await refreshWorkspaces()
                handlePickWorkspace(ws._id)
              }}
              keyboardCursor={keyboardCursor}
              flatVisibleWorkspaces={flatVisibleWorkspaces}
            />
          ))
        )}
      </div>
    </div>
  )

  const trigger =
    variant === 'inline'
      ? triggerInline
      : variant === 'header-tile'
      ? triggerHeaderTile
      : triggerHeader

  return (
    <div className="relative" ref={rootRef}>
      {trigger}
      {popoverBody}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Org section — header (multi-org only) + workspace rows + role-gated
// `+ New workspace` footer. Pure presentational; all state lives in
// the parent.
// ──────────────────────────────────────────────────────────────────────

function OrgSection({
  org,
  workspaces,
  isMultiOrg,
  isSearching,
  activeWorkspaceId,
  creatingInOrgId,
  setCreatingInOrgId,
  onPickWorkspace,
  onCreated,
  keyboardCursor,
  flatVisibleWorkspaces,
}: {
  org: OrgWithRole
  workspaces: WorkspaceSummary[]
  isMultiOrg: boolean
  isSearching: boolean
  activeWorkspaceId: string
  creatingInOrgId: string | null
  setCreatingInOrgId: (id: string | null) => void
  onPickWorkspace: (id: string) => void
  onCreated: (ws: WorkspaceSummary) => void | Promise<void>
  keyboardCursor: number
  flatVisibleWorkspaces: WorkspaceSummary[]
}) {
  // Role-gated affordance — same matrix the backend
  // `requireOrgPermission` middleware reads, so UI visibility cannot
  // drift from API enforcement. Hidden during search (creating
  // mid-filter is rarely the user's intent).
  const canCreate = useCanI('workspaces.create', org._id)
  const showCreate = canCreate && !isSearching

  return (
    <div className="pb-1">
      {/* Header is shown ONLY in the multi-org case. Single-org users
          get a top-of-popover "in <Org>" cue instead (rendered by the
          parent). */}
      {isMultiOrg && (
        <div className="flex items-center justify-between px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <span>{org.name}</span>
          <span className="text-[10px] font-normal normal-case tracking-normal px-1.5 py-px rounded-full bg-muted/60 text-foreground/80">
            {org.role}
          </span>
        </div>
      )}
      {workspaces.map((ws) => {
        const isActive = ws._id === activeWorkspaceId
        const flatIdx = flatVisibleWorkspaces.indexOf(ws)
        const isCursor = flatIdx === keyboardCursor
        return (
          <button
            key={ws._id}
            type="button"
            onClick={() => onPickWorkspace(ws._id)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted/50 text-left ${
              isCursor ? 'bg-muted/40' : ''
            } ${isActive ? 'bg-primary/10 font-medium' : ''}`}
            aria-current={isActive ? 'true' : undefined}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                isActive ? 'bg-primary' : 'bg-transparent'
              }`}
              aria-hidden="true"
            />
            <span className="truncate flex-1">{ws.name}</span>
            {isActive && (
              <span className="text-[10px] text-muted-foreground shrink-0">
                current
              </span>
            )}
          </button>
        )
      })}
      {showCreate && (
        <div className="px-1 mt-1">
          {creatingInOrgId === org._id ? (
            <CreateWorkspaceInline
              orgId={org._id}
              onCancel={() => setCreatingInOrgId(null)}
              onCreated={onCreated}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCreatingInOrgId(org._id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground rounded"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>
                {isMultiOrg ? `New workspace in ${org.name}` : 'New workspace'}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function CreateWorkspaceInline({
  orgId,
  onCancel,
  onCreated,
}: {
  orgId: string
  onCancel: () => void
  onCreated: (ws: WorkspaceSummary) => void | Promise<void>
}) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setSubmitting(true)
    setError(null)
    try {
      const ws = await workspacesAPI.createInOrg(orgId, trimmed)
      await onCreated(ws)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed')
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-1.5">
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Workspace name (e.g. Marketing)"
          autoFocus
          disabled={submitting}
          className="flex-1 h-7 px-2 text-sm bg-muted/50 rounded focus:outline-hidden focus:ring-1 focus:ring-ring"
        />
        <button
          type="button"
          onClick={onCancel}
          className="p-1 text-muted-foreground hover:text-foreground"
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <button
        type="submit"
        disabled={!name.trim() || submitting}
        className="w-full text-xs h-7 bg-primary text-primary-foreground rounded disabled:opacity-50 hover:bg-primary/90"
      >
        {submitting ? 'Creating…' : 'Create'}
      </button>
    </form>
  )
}
